/**
 * 서버가 거절한 이유를 **사람이 읽을 문장**으로 고른다.
 *
 * 예전에는 로그인 화면과 회원가입 신청 화면이 실패를 전부 «잠시 후 다시
 * 시도해주세요» 로 덮었다. 인증번호를 틀린 사람도 그 문장을 봤다 — 번호를 고치는
 * 대신 **기다렸다가 같은 번호로 다시 누르고**, 누를 때마다 횟수 한도(10분 5회)가
 * 깎였다(2026-09-23 수동 TC GL-04 · S-07).
 *
 * ## 서버 문장을 그대로 보여 줘도 되는가
 *
 * 된다. 이 두 문(`/auth/magic/request` · `/signup-requests`)은 계정을 **응답을 보낸
 * 뒤에** 조회한다. 응답 전에 나올 수 있는 거절은 «너무 잦다» · «KCB 가 번호를
 * 거절했다» · «인증이 필요하다» 셋뿐이고, 어느 것도 그 이메일이 가입돼 있는지와
 * 상관이 없다.
 *
 * ## 코드는 보여 주지 않는다
 *
 * `IDENTITY_REQUIRED` 같은 값은 화면이 분기하라고 주는 **코드**다. 사람에게 그대로
 * 띄우면 고장 난 화면처럼 보인다. 대문자·밑줄로만 된 값은 코드로 보고 버린다.
 * 배열(422 검증 오류)도 버린다 — 그대로 그리면 React 가 터진다.
 */

/** 서버가 «이 요청에는 본인인증이 필요하다» 고 답할 때 쓰는 코드. */
export const IDENTITY_REQUIRED = "IDENTITY_REQUIRED";

const LOOKS_LIKE_CODE = /^[A-Z0-9_]+$/;

/** 서버가 죽었을 때(5xx, 읽을 문장 없음)의 기본 문장. «인증에 실패했습니다» 와 갈라야 한다. */
export const SERVER_TROUBLE = "지금 서버에 문제가 있어 처리하지 못했습니다. 잠시 후 다시 시도해주세요.";

/**
 * 응답을 **아예 못 받았을 때**(연결 끊김 · 브라우저가 막은 교차 출처 응답). 상태 코드가 없다.
 * «인증에 실패했습니다» · «링크를 쓸 수 없습니다» 로 떨어지면 사람은 틀린 쪽으로 간다.
 */
export const CONNECTION_TROUBLE =
  "서버와 연결하지 못했습니다. 인터넷 연결을 확인하고 잠시 후 다시 시도해주세요.";

/**
 * 서버가 **입력 형식**을 거절했다(422 — 본문은 배열이라 사람에게 못 보여 준다). 기다려도
 * 안 된다 — «잠시 후 다시 시도» 라고 하면 같은 입력으로 영원히 다시 누른다(f1-4).
 */
export const INVALID_INPUT = "입력한 내용을 다시 확인해주세요.";

/** 응답을 못 받았거나 5xx — «서버 쪽 문제» 로 가를 때. 링크 화면처럼 문장을 따로 쓰는 곳이 쓴다. */
export const isServerTrouble = (error: any): boolean =>
  Boolean(error?.noResponse) || Number(error?.httpStatus) >= 500;

export const failureMessage = (
  error: any,
  {
    fallback,
    identityRequired = fallback,
    serverTrouble = SERVER_TROUBLE,
  }: {
    /** 서버가 읽을 만한 문장을 주지 않았을 때 */
    fallback: string;
    /** 서버가 `IDENTITY_REQUIRED` 로 답했을 때 */
    identityRequired?: string;
    /**
     * 서버가 **죽었을 때**(5xx 인데 읽을 문장이 없다). `fallback` 과 다른 문장을 쓴다.
     *
     * 예전에는 이메일 찾기에서 메일 발송이 터져 500 이 났을 때 «인증에 실패했습니다» 가
     * 떴다(2026-09-23 B1·B7). 인증은 **성공한 뒤**였다 — 사람을 틀린 쪽으로 보낸다.
     */
    serverTrouble?: string;
  },
): string => {
  const detail = error?.detail;
  if (detail === IDENTITY_REQUIRED) return identityRequired;
  if (typeof detail === "string" && detail.trim() && !LOOKS_LIKE_CODE.test(detail)) {
    return detail;
  }
  if (error?.noResponse) return CONNECTION_TROUBLE;
  if (Number(error?.httpStatus) === 422) return INVALID_INPUT;
  if (Number(error?.httpStatus) >= 500) return serverTrouble;
  return fallback;
};
